import { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';

import DragDropDialog from './dialogs/DragDropDialog';
import DocumentUploadIcon from '../../assets/images/document-upload.svg';
import { FHButton, FHInputField } from '../base';
import { Themes } from '../../constants';
import { getThemeColor } from '../../utils/common';
import { FORTA_WHITE } from '../../utils/colors';

import type { DropzoneProps, FileRejection } from 'react-dropzone';

type DropzoneSelectedFile = File & {
  preview?: string;
  noPreviewFile?: boolean;
};

interface FDropzoneProps extends DropzoneProps {
  colorTheme: Themes;
  show: boolean;
  title?: string;
  subTitle?: string;
  submitText?: string;
  dropzoneHeading?: string;
  dropzoneHint?: string;
  fileNameRegEx?: string;
  noPreviewFiles?: string[];
  onClose: () => void;
  onSelect: (files: DropzoneSelectedFile[]) => void;
}

const FDropzone = ({
  show,
  title,
  subTitle,
  submitText = 'Select Visual',
  dropzoneHeading = 'Drag visual here',
  dropzoneHint = 'or click to browse',
  colorTheme = Themes.Primary,
  maxFiles = 1,
  maxSize = 10, // In MB
  accept,
  fileNameRegEx = '^[a-zA-Z0-9_., -]*$',
  noPreviewFiles = [],
  onSelect,
  onClose,
}: FDropzoneProps) => {
  const [selectedFiles, setSelectedFiles] = useState<DropzoneSelectedFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const handleDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (
        fileRejections.some(({ errors }) =>
          errors.some(({ code }) => code === 'too-many-files'),
        )
      ) {
        setSelectedFiles([]);
        setErrors([`Maximum number of files allowed: ${maxFiles}`]);
        return;
      }

      if (acceptedFiles.length === 0) {
        if (
          fileRejections.some(({ errors }) =>
            errors.some(({ code }) => code === 'file-too-large'),
          )
        ) {
          setErrors([`maximum file size allowed is ${maxSize} Mb`]);
        }
        return;
      }

      const files = acceptedFiles.map((file: File) => {
        const type = file.type.split('/')[1];
        if (noPreviewFiles.includes(`.${type}`)) {
          return Object.assign(file, {
            noPreviewFile: true,
          });
        } else {
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
        }
      });
      setSelectedFiles(files);
      const errorList = [];
      const regex = new RegExp(fileNameRegEx);

      if (!regex.test(files[0].name)) {
        errorList.push('File name should only contain Numbers, Alphabets and -,_ only');
      }

      setErrors(errorList);
    },
    [fileNameRegEx, maxFiles, maxSize, noPreviewFiles],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    maxFiles,
    maxSize: maxSize * 1024 * 1024,
    accept,
    noPaste: true,
    onDrop: handleDrop,
  });

  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [selectedFiles]);

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
    setErrors([]);
  };

  const handleSelect = () => {
    onSelect(selectedFiles);
    handleClose();
  };

  return (
    <DragDropDialog
      open={show}
      onClose={handleClose}
      colorTheme={colorTheme}
      title={title ? title : 'Upload visual'}
      subTitle={subTitle ? subTitle : 'Upload a visual to add to the assessment'}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          width: '100%',
          height: 300,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: getThemeColor(colorTheme).light3,
          borderWidth: '1px',
          borderStyle: 'dashed',
          borderColor: getThemeColor(colorTheme).primary,
          borderRadius: '10px',
          p: 2,
          '& ::-webkit-scrollbar-track': {
            background: FORTA_WHITE,
          },
          '& ::-webkit-scrollbar-thumb': {
            background: getThemeColor(colorTheme).primary,
            borderColor: FORTA_WHITE,
          },
        }}
        textAlign="center"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} id="dropzone_input" />
        {selectedFiles.length > 0 ? (
          <Box sx={{ height: '100%', overflowY: 'scroll' }}>
            {selectedFiles.map((file) => {
              if (file.noPreviewFile) {
                return (
                  <>
                    <Typography fontWeight={'bold'} sx={{ width: '100%', height: 40 }}>
                      Preview not available for:
                    </Typography>
                    <Typography fontWeight={'bold'} sx={{ width: '100%', height: 40 }}>
                      {file.name}
                    </Typography>
                  </>
                );
              } else {
                return (
                  <img
                    src={file.preview}
                    style={{ objectFit: 'contain', width: '100%', marginBottom: '1em' }}
                    alt="preview"
                    key={file.name}
                  />
                );
              }
            })}
          </Box>
        ) : (
          <>
            <img src={DocumentUploadIcon} alt="upload-icon" width={250} height={250} />
            <Typography variant="h4">{dropzoneHeading}</Typography>
            <Typography>{dropzoneHint}</Typography>
          </>
        )}
      </Box>
      <Box sx={{ width: '100%' }}>
        <Box mt={2} sx={{ display: 'flex' }}>
          <FHInputField
            disabled
            fullWidth
            coloredBackground
            colorTheme={colorTheme}
            inputHeight={40}
            sx={{ flexGrow: 1, mr: '1em' }}
            value={selectedFiles.map((file) => file.name).join(', ')}
            data-testid="visualInputField"
          />
          <FHButton colorTheme={colorTheme} onClick={open} data-testid="browseButton">
            Browse
          </FHButton>
        </Box>
        <Box mt={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            colorTheme={colorTheme}
            onClick={() => {
              handleClose();
            }}
            secondary
            sx={{ mr: '1em' }}
          >
            Cancel
          </FHButton>
          <FHButton
            data-testid="selectVisualButton"
            colorTheme={colorTheme}
            onClick={handleSelect}
            disabled={Boolean(errors.length)}
          >
            {submitText}
          </FHButton>
        </Box>
      </Box>
      {errors.length > 0 &&
        errors.map((error, index) => (
          <Typography key={index} fontWeight={'bold'}>
            {error}
          </Typography>
        ))}
    </DragDropDialog>
  );
};

export default FDropzone;
